import React, { useEffect, useContext, useState } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useMediaQuery, useTheme } from "@mui/material";
import SideBar from "./sideBar";
import PopMenu from "./popMenu";
import ModelsScreen from "./modelsScreen";
import CreditsScreen from "./creditsScreen";
import PipelineScreen from "./pipelineScreen";
import HqScreen from "./hqScreen";
import {
  UserContext,
  updateUserQualification,
} from "../../../contexts/userContext";
import QualificationPopup, {
  QualificationData,
} from "../../utils/qualificationPopup";
import PaywallOverlay from "../../utils/paywallOverlay";
import { getDocs, query, orderBy } from "firebase/firestore";
import ModelDb from "../../../db/modelDb";
import { Model } from "../../../models/model";

export default function ThumbGenieApp() {
  const navigate = useNavigate();
  const auth = getAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const user = useContext(UserContext);
  const [showQualification, setShowQualification] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [models, setModels] = useState<Model[]>([]);
  const [hasCheckedModels, setHasCheckedModels] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate("/thumb-genie");
      }
    });

    return () => {
      unsubscribe();
    };
  }, [auth, navigate]);

  useEffect(() => {
    if (user && !user.qualificationData) {
      setShowQualification(true);
    } else {
      setShowQualification(false);
    }
  }, [user?.qualificationData]);

  useEffect(() => {
    if (user && user.credits < 4) {
      setShowPaywall(true);
    } else {
      setShowPaywall(false);
    }
  }, [user?.credits]);

  // Check for models when user is available
  useEffect(() => {
    const fetchModels = async () => {
      if (user) {
        try {
          const modelDb = new ModelDb();
          const modelsQuery = query(
            modelDb.collection({ userId: user.id, modelId: "" }),
            orderBy("createdAt", "desc")
          );
          const querySnapshot = await getDocs(modelsQuery);
          const modelsData = querySnapshot.docs.map((doc) =>
            Model.fromFirestore(doc)
          );
          setModels(modelsData);
          setHasCheckedModels(true);
        } catch (error) {
          console.error("Error fetching models:", error);
          setHasCheckedModels(true);
        }
      }
    };

    fetchModels();
  }, [user]);

  // Redirect based on model availability
  useEffect(() => {
    if (
      hasCheckedModels &&
      user &&
      window.location.pathname === "/app/thumb-genie"
    ) {
      if (models.length > 0) {
        navigate("/app/thumb-genie/pipeline");
      } else {
        navigate("/app/thumb-genie/models");
      }
    }
  }, [hasCheckedModels, models, user, navigate]);

  const handleQualificationSubmit = async (data: QualificationData) => {
    if (user) {
      await updateUserQualification(user.id, data);
      setShowQualification(false);
    }
  };

  const DefaultRoute = () => {
    // Show models screen while checking models or if no models
    if (!hasCheckedModels || models.length === 0) {
      return <ModelsScreen />;
    }
    // Show pipeline if user has models
    return <PipelineScreen />;
  };

  return (
    <div className="flex">
      {!isMobile && <SideBar />}
      <div className="flex-1">
        <PopMenu />
        <QualificationPopup
          open={showQualification}
          email={user?.email || ""}
          onClose={() => setShowQualification(false)}
          onSubmit={handleQualificationSubmit}
          currentApp="ThumbGenie"
        />
        {showPaywall && user && (
          <PaywallOverlay
            currentCredits={user.credits}
            userId={user.id}
            onClose={() => setShowPaywall(false)}
          />
        )}
        <Routes>
          <Route path="/" element={<DefaultRoute />} />
          <Route path="/pipeline" element={<PipelineScreen />} />
          <Route path="/models" element={<ModelsScreen />} />
          <Route path="/credits" element={<CreditsScreen />} />
          <Route path="/hq" element={<HqScreen />} />
          <Route path="*" element={<ModelsScreen />} />
        </Routes>
      </div>
    </div>
  );
}
